import { FormEvent, useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, InputAdornment, Link as MuiLink, Stack, TextField } from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import { isAxiosError } from 'axios';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import api from '../../../api/axiosInstance';
import { showError } from '../../../components/notifications';
import { RECAPTCHA_SITE_KEY } from '../../../constants';
import { useAuth } from '../../../provider/AuthProvider';
import { emailRegex } from '../../../utils/regex';
import { Loader } from '../../../components/loader';

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
}

const PASSWORD_MIN = 6;

export default function Login() {
  const { login } = useAuth();
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [touched, setTouched] = useState({ email: false, password: false });
  const [isFormValid, setIsFormValid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { mutate, isPending } = useMutation<LoginResponse, Error, void>({
    mutationFn: () =>
      api
        .post<LoginResponse>('/auth/login', { email, password, recaptchaToken })
        .then(res => res.data),
    onSuccess: async data => {
      await login(data);
    },
    onError: err => {
      if (isAxiosError(err)) {
        showError(err.response?.data?.message ?? err.message);
      } else {
        showError(err.message);
      }
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setTouched({ email: true, password: true });

    if (!emailRegex.test(email)) {
      showError('Некорректный email');
      return;
    }
    if (password.length < PASSWORD_MIN) {
      showError(`Пароль должен содержать минимум ${PASSWORD_MIN} символов`);
      return;
    }
    if (!recaptchaToken) {
      showError('Подтвердите reCAPTCHA');
      return;
    }

    mutate();
  };

  const isEmailValid = emailRegex.test(email);
  const isPasswordValid = password.length >= PASSWORD_MIN;

  useEffect(() => {
    setIsFormValid(Boolean(isEmailValid && isPasswordValid && recaptchaToken));
  }, [isEmailValid, isPasswordValid, recaptchaToken]);

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={2}>
        <TextField
          fullWidth
          label="Email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          onBlur={() => setTouched(t => ({ ...t, email: true }))}
          error={touched.email && !isEmailValid}
          helperText={touched.email && !isEmailValid ? 'Введите корректный email' : ' '}
          InputLabelProps={{ shrink: true }}
          disabled={isPending}
          autoComplete="email"
        />

        <TextField
          fullWidth
          label="Пароль"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={e => setPassword(e.target.value)}
          onBlur={() => setTouched(t => ({ ...t, password: true }))}
          error={touched.password && !isPasswordValid}
          helperText={touched.password && !isPasswordValid ? `Минимум ${PASSWORD_MIN} символов` : ' '}
          InputLabelProps={{ shrink: true }}
          disabled={isPending}
          autoComplete="current-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                  onClick={() => setShowPassword(v => !v)}
                  edge="end"
                  disabled={isPending}
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ overflowX: 'auto' }}>
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={RECAPTCHA_SITE_KEY}
            onChange={token => setRecaptchaToken(token)}
          />
        </Box>

        <MuiLink component={Link} to="/forgetpassword" underline="hover" sx={{ alignSelf: 'flex-start' }}>
          Забыли пароль?
        </MuiLink>

        <Button type="submit" variant="contained" fullWidth disabled={!isFormValid || isPending} size="large">
          {isPending ? <Loader /> : 'Войти'}
        </Button>
      </Stack>
    </Box>
  );
}
